import { useMemo, useState } from "react";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { getRandomColor, selectableColors } from "@/design/colors/colors";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import ColorPicker from "@/design/forms/ColorPicker";
import FormGroup from "@/design/forms/FormGroup";
import InputSelect from "@/design/forms/InputSelect";
import { any, string } from "@/utils/hooks/endorse";
import useSave, { SaveMode } from "@/utils/hooks/useSave";
import { createArray } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viArrowRight, viCalendarStars } from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { addDays, format, isWithinInterval, startOfDay } from "date-fns";
import Holidays from "date-holidays";
import PropTypes from "prop-types";

import CountryFlag from "./CountryFlag";
import publicHolidayReducer from "./publicHolidayReducer";

const currentYear = new Date().getFullYear();
const initialYears = createArray(currentYear, 4);

function generateInitialValue() {
  return {
    years: initialYears,
    language: "default",
    color: getRandomColor()[500],
    overflow: null,
  };
}

function PublicHolidayDialog({ dialogOpen, holidaySet, closeHolidayDialog, holidaySetDispatch }) {
  const holidays = useMemo(() => new Holidays(), []);

  const [publicPreExistingDates, setPublicPreExistingDates] = useState([]);
  const [publicNewDates, setPublicNewDates] = useState([]);
  const [publicReview, setPublicReview] = useState(false);

  const resetPublicDialog = () => {
    setPublicReview(false);
    setPublicPreExistingDates([]);
    setPublicNewDates([]);
    dispatch({ type: "set", value: generateInitialValue() });
    closeHolidayDialog();
  };

  const {
    value: publicHoliday,
    dispatch,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: generateInitialValue(),
    mode: SaveMode.MANUAL,
    reducer: publicHolidayReducer,
    validator: () => ({
      years: any().truthy(publicHoliday.years.length >= 1, { message: "You must selected at least 1 year." }),
      country: string().required({ message: "Country is required." }),
      state: string().required({
        message: "State is required.",
        condition: publicHoliday.country !== "" && states.length > 0,
      }),
      language: string().required({ message: "language is required." }),
      color: string().required({ message: "Color is required." }),
    }),
    onSubmit: () => Api.storeHolidaySetHoliday(holidaySet, publishableHolidays),
    onSuccess: () => {
      Promise.resolve(Api.getHolidaySet(holidaySet)).then((_response) => {
        holidaySetDispatch({ type: "set", value: _response.data.holidaySet });
        resetPublicDialog();
      });
    },
  });

  const availableYears = useMemo(() => initialYears, []);

  const countries = Object.entries(holidays.getCountries("en")).map(([code, name]) => ({ code, name }));
  const states = publicHoliday.country
    ? Object.entries(holidays.getStates(publicHoliday.country) || {}).map(([code, name]) => ({ code, name }))
    : [];

  // Review process
  const reviewPublicHolidays = () => {
    const existingDates = [];
    const newDates = [];

    const publicHolidays = new Holidays(publicHoliday.country, publicHoliday.state);
    publicHolidays.setLanguages(publicHoliday.language === "default" ? undefined : "en");

    publicHoliday.years.map((year) =>
      publicHolidays.getHolidays(year).map((_day) => {
        if (_day.type === "public" || _day.type === "bank") {
          const duplicate = holidaySet.holidays.find((_holiday) =>
            isWithinInterval(format(_day.date, "yyyy-MM-dd"), { start: _holiday.start, end: _holiday.end }),
          );

          const newHoliday = {
            color: publicHoliday.color,
            overflow: publicHoliday.overflow,
            name: _day.name,
            start: format(startOfDay(_day.date), "yyyy-MM-dd HH:mm:ss"),
            end: format(startOfDay(addDays(_day.date, 1)), "yyyy-MM-dd HH:mm:ss"),
            publish: !duplicate,
          };

          if (duplicate) {
            existingDates.push(newHoliday);
          } else {
            newDates.push(newHoliday);
          }
        }
      }),
    );

    setPublicReview(true);
    setPublicPreExistingDates(existingDates);
    setPublicNewDates(newDates);
  };

  const publishableHolidays = [
    ...publicPreExistingDates.filter((h) => h.publish),
    ...publicNewDates.filter((h) => h.publish),
  ];

  return (
    <>
      <Dialog onClose={resetPublicDialog} open={dialogOpen} maxWidth="sm">
        <HeaderCard>
          <CardHeader
            avatar={<Avatar text="Public holidays" icon={viCalendarStars} color={publicHoliday.color} />}
            title="Public holidays"
          />
        </HeaderCard>
        {!publicReview ? (
          <Stack
            sx={{
              gap: 2,
              mt: 2,
            }}
          >
            <FormGroup title="Years">
              <Grid size={12}>
                <List sx={{ flexDirection: "row", gap: 1 }}>
                  {availableYears.map((year) => (
                    <ListItem disablePadding sx={{ minHeight: 36 }} key={year}>
                      <ListItemButton
                        sx={{ minHeight: 36, py: 0.5 }}
                        onClick={() => dispatch({ type: "year", value: year })}
                      >
                        <ListItemIcon sx={{ p: 0, m: 0, minHeight: 0, minWidth: 26 }}>
                          <Checkbox
                            edge="start"
                            checked={publicHoliday.years.includes(year)}
                            tabIndex={-1}
                            disableRipple
                            sx={{ py: 0, px: 0.5 }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={year} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </FormGroup>
            <FormGroup title="Country">
              <Grid size={12}>
                <Card
                  variant="outlined"
                  sx={{
                    maxHeight: publicHoliday.country ? 150 : 400,
                    transition: "all .2s ease-in-out",
                    overflow: "auto",
                  }}
                >
                  <List sx={{ p: 1 }}>
                    {countries
                      .filter((country) => ["BE", "NL", "LU", "DE", "CH", "GB", "US"].includes(country.code))
                      .map((country) => (
                        <ListItem disablePadding sx={{ minHeight: 36 }} key={country.name}>
                          <ListItemButton
                            sx={{ minHeight: 36, py: 0.5 }}
                            onClick={() => dispatch({ type: "country", value: country.code })}
                            selected={publicHoliday.country === country.code}
                          >
                            <ListItemIcon sx={{ py: 0 }}>
                              <CountryFlag code={country.code} style={{ marginTop: 2 }} />
                            </ListItemIcon>
                            <ListItemText primary={country.name} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    <Divider sx={{ m: 0.5 }} />
                    {countries.map((country) => (
                      <ListItem disablePadding sx={{ minHeight: 36 }} key={country.name}>
                        <ListItemButton
                          sx={{ minHeight: 36, py: 0.5 }}
                          onClick={() => dispatch({ type: "country", value: country.code })}
                          selected={publicHoliday.country === country.code}
                        >
                          <ListItemIcon sx={{ py: 0 }}>
                            <CountryFlag code={country.code} style={{ marginTop: 2 }} />
                          </ListItemIcon>
                          <ListItemText primary={country.name} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Grid>
              {publicHoliday.country !== "" && states.length > 0 && (
                <Grid size={12}>
                  <InputSelect
                    id="state"
                    label="State"
                    onChange={(id, value) => dispatch({ type: id, value })}
                    value={publicHoliday.state}
                    selectValue="code"
                    selects={states}
                    getOptionLabel={(option) => option.name}
                    error={errors}
                  />
                </Grid>
              )}
            </FormGroup>
            <FormGroup title="Customization">
              <Grid size={{ xs: 12, md: 6 }}>
                <InputSelect
                  id="language"
                  label="Language"
                  onChange={(id, value) => dispatch({ type: id, value })}
                  value={publicHoliday.language}
                  selectValue="value"
                  selects={[
                    { value: "default", title: "Localized" },
                    { value: "en", title: "English" },
                  ]}
                  getOptionLabel={(option) => option.title}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <OverflowDrawer
                  label="Destination"
                  value={publicHoliday.overflow}
                  propertyName="overflow"
                  overflowHandler={(value) => dispatch({ type: "overflow", value })}
                />
              </Grid>
              <Grid size={12}>
                <ColorPicker
                  colors={selectableColors}
                  select={(value) => dispatch({ type: "color", value })}
                  value={publicHoliday.color}
                />
              </Grid>
            </FormGroup>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }} />
              <Stack
                direction="row"
                sx={{
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <Button onClick={resetPublicDialog} variant="outlined">
                  Discard
                </Button>
                <Button
                  onClick={reviewPublicHolidays}
                  variant="contained"
                  disabled={Object.keys(allErrors).length > 0}
                  endIcon={<Valkyrie icon={viArrowRight} />}
                >
                  Review
                </Button>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack
            sx={{
              gap: 2,
              mt: 2,
            }}
          >
            {publicNewDates.length >= 1 && (
              <FormGroup title={`New holidays \u00b7 ${publicNewDates.length}`}>
                <Grid size={12}>
                  <Typography sx={{ mb: 1 }}>The following holidays will be added.</Typography>
                  <Card
                    variant="outlined"
                    sx={{
                      maxHeight: 400,
                      transition: "all .2s ease-in-out",
                      overflow: "auto",
                    }}
                  >
                    <List sx={{ p: 1 }}>
                      {publicNewDates.map((newDate) => (
                        <ListItem disablePadding sx={{ minHeight: 36 }} key={newDate.start}>
                          <ListItemButton
                            sx={{ minHeight: 36, py: 0.5 }}
                            onClick={() => {
                              setPublicNewDates(
                                publicNewDates.map((holiday) =>
                                  holiday.start === newDate.start ? { ...holiday, publish: !holiday.publish } : holiday,
                                ),
                              );
                            }}
                          >
                            <ListItemIcon sx={{ p: 0, m: 0, minHeight: 0, minWidth: 26 }}>
                              <Checkbox
                                edge="start"
                                checked={newDate.publish}
                                tabIndex={-1}
                                disableRipple
                                sx={{ py: 0, px: 0.5 }}
                              />
                            </ListItemIcon>
                            <ListItemText primary={`${newDate.name} \u00b7 ${format(newDate.start, "d MMMM yyyy")}`} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </Grid>
              </FormGroup>
            )}
            {publicPreExistingDates.length >= 1 && (
              <FormGroup title={`Duplicate holidays \u00b7 ${publicPreExistingDates.length}`} collapsible={true}>
                <Grid size={12}>
                  <Typography sx={{ mb: 1 }}>
                    The following holidays overlap with pre-existing days in this holiday set. Select the ones that you
                    still would like to add, but be aware of unexpected behavior.
                  </Typography>
                  <Card
                    variant="outlined"
                    sx={{
                      maxHeight: 400,
                      transition: "all .2s ease-in-out",
                      overflow: "auto",
                    }}
                  >
                    <List sx={{ p: 1 }}>
                      {publicPreExistingDates.map((existingDate) => (
                        <ListItem disablePadding sx={{ minHeight: 36 }} key={existingDate.start}>
                          <ListItemButton
                            sx={{ minHeight: 36, py: 0.5 }}
                            onClick={() => {
                              setPublicPreExistingDates(
                                publicPreExistingDates.map((holiday) =>
                                  holiday.start === existingDate.start
                                    ? { ...holiday, publish: !holiday.publish }
                                    : holiday,
                                ),
                              );
                            }}
                          >
                            <ListItemIcon sx={{ p: 0, m: 0, minHeight: 0, minWidth: 26 }}>
                              <Checkbox
                                edge="start"
                                checked={existingDate.publish}
                                tabIndex={-1}
                                disableRipple
                                sx={{ py: 0, px: 0.5 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${existingDate.name} \u00b7 ${format(existingDate.start, "d MMMM yyyy")}`}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </Grid>
              </FormGroup>
            )}
            <Stack
              direction="row"
              sx={{
                justifyContent: "flex-end",
                gap: 1,
                containerType: "inline-size",
              }}
            >
              <Button
                onClick={() => setPublicReview(false)}
                variant="outlined"
                startIcon={<Valkyrie icon={viArrowLeft} />}
              >
                Back
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button onClick={resetPublicDialog} variant="outlined">
                Discard
              </Button>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
            </Stack>
          </Stack>
        )}
      </Dialog>
    </>
  );
}

PublicHolidayDialog.propTypes = {
  dialogOpen: PropTypes.bool,
  holidaySet: PropTypes.object,
  closeHolidayDialog: PropTypes.func,
  holidaySetDispatch: PropTypes.func,
};

export default PublicHolidayDialog;
